<div class="modal-container">
  <div class="modal-header">
    <h2>Add new sources from Veeva</h2>
    <button
      type="button"
      class="modal-close"
      aria-label="Close Veeva import"
      [disabled]="importBusy()"
      (click)="onClose()"
    >
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <div class="modal-content">
    <div class="sync-info-banner" role="note">
      <mat-icon aria-hidden="true">info</mat-icon>
      <div>
        <strong>Manual sync only</strong>
        <p>
          Veeva import does not run on an automatic hourly or daily schedule like other file syncs.
          Documents are imported only when you run this flow — there is no recurring sync from here.
        </p>
      </div>
    </div>

    <mat-stepper [linear]="true" #stepper class="veeva-stepper">
      <mat-step label="Search" [completed]="canProceedToNextStep()">
        <app-veeva-dynamic-filters
          [disabled]="previewLoading()"
          (filtersChanged)="onFiltersChanged($event)"
          (loadingChange)="filtersLoading.set($event)"
        />

        @if (previewLoading() || filtersLoading()) {
          <mat-progress-bar mode="indeterminate" aria-label="Loading Veeva data" />
        }

        <div class="search-actions">
          <button
            mat-raised-button
            color="accent"
            type="button"
            [disabled]="previewLoading() || filtersLoading()"
            (click)="onSearch()"
          >
            Search
          </button>
        </div>

        @if (pagination(); as page) {
          <div class="import-mode">
            <mat-checkbox
              [checked]="importAllByQuery()"
              [disabled]="previewLoading() || page.total === 0"
              (change)="onImportAllByQueryChange($event)"
            >
              Import up to {{ importLimit }} of {{ page.total }} matching documents
            </mat-checkbox>
          </div>
          @if (page.total === 0 && !previewLoading()) {
            <div class="empty-results" role="status">No matching documents found.</div>
          }
        }

        @if (results().length > 0) {
          <div class="found-results-container">
            <section class="results-container" aria-labelledby="veeva-results-heading">
              <h3 id="veeva-results-heading">{{ pagination()?.total ?? 0 }} items found</h3>
              <div class="results">
                @for (item of results(); track item.externalId) {
                  <div class="result">
                    <mat-checkbox
                      [checked]="isSelected(item)"
                      [disabled]="importAllByQuery() || previewLoading()"
                      (change)="onCheckboxChange($event, item)"
                    >
                      {{ item.name }}
                    </mat-checkbox>
                  </div>
                }

                @if (pagination(); as page) {
                  @if (page.total > page.limit) {
                    <mat-paginator
                      [length]="page.total"
                      [pageSize]="page.limit"
                      [pageIndex]="page.page - 1"
                      [showFirstLastButtons]="true"
                      [pageSizeOptions]="[10, 20, 50]"
                      [disabled]="previewLoading()"
                      (page)="onPageChange($event.pageIndex + 1, $event.pageSize)"
                      class="pagination-container"
                    />
                  }
                }
              </div>
            </section>

            <section class="selected-items-container" aria-labelledby="veeva-selected-heading">
              <h3 id="veeva-selected-heading">
                Selected items ({{ selectedDocuments().length }})
              </h3>
              <div class="selected-items-list">
                @for (item of selectedDocuments(); track item.externalId) {
                  <div class="selected-item">
                    <button
                      type="button"
                      class="remove-selected"
                      [attr.aria-label]="'Remove ' + item.name"
                      (click)="removeSelectedItem(item.externalId)"
                    >
                      <mat-icon>close</mat-icon>
                    </button>
                    <span>{{ item.name }}</span>
                  </div>
                }
              </div>
            </section>
          </div>
        }

        @if (results().length > 0) {
          <div class="step-actions">
            <button
              mat-raised-button
              color="accent"
              type="button"
              matStepperNext
              [disabled]="!canProceedToNextStep()"
            >
              Next
            </button>
          </div>
        }
      </mat-step>

      <mat-step label="Create & Import" [stepControl]="folderForm" class="veeva-step">
        <div class="create-import-container">
          <section class="folder-form-section" aria-labelledby="veeva-folder-heading">
            <h3 id="veeva-folder-heading">Create Source Folder</h3>
            <form class="form-field-full" [formGroup]="folderForm">
              <div class="form-inputs">
                <div class="form-field-container">
                  <label class="form-field-label" for="veeva-folder-name">Source name</label>
                  <mat-form-field
                    class="gingr-input-wrapper compact-field w-full"
                    appearance="outline"
                    subscriptSizing="dynamic"
                  >
                    <input
                      matInput
                      id="veeva-folder-name"
                      placeholder="Source name"
                      type="text"
                      formControlName="name"
                      required
                    />
                    @if (folderForm.controls.name.hasError('required')) {
                      <mat-error>Please enter source name</mat-error>
                    }
                  </mat-form-field>
                </div>

                <div class="form-field-container">
                  <label class="form-field-label" for="veeva-folder-status">Status</label>
                  <mat-form-field
                    class="gingr-input-wrapper compact-field w-full"
                    appearance="outline"
                    subscriptSizing="dynamic"
                  >
                    <mat-select id="veeva-folder-status" formControlName="status" placeholder="Select status">
                      @for (status of statuses; track status) {
                        <mat-option [value]="status">{{ status | titlecase }}</mat-option>
                      }
                    </mat-select>
                    @if (folderForm.controls.status.hasError('required')) {
                      <mat-error>Please provide status</mat-error>
                    }
                  </mat-form-field>
                </div>
              </div>
            </form>
          </section>

          <section class="import-status-section" aria-labelledby="veeva-import-status-heading">
            <h3 id="veeva-import-status-heading">Import Status</h3>
            <div class="import-status-content" aria-live="polite">
              @if (!canProceedImport()) {
                <div class="info">
                  <mat-icon class="error">error</mat-icon>
                  <span>Fill all required fields and choose documents to import.</span>
                </div>
              } @else if (importBusy()) {
                <app-spinner [message]="null" [small]="true" />
              } @else if (importState() === 'failed') {
                <div class="info">
                  <mat-icon class="error">error</mat-icon>
                  <span>Import failed.</span>
                </div>
              } @else if (importState() === 'no-matches') {
                <div class="info">
                  <mat-icon>info</mat-icon>
                  <span>No documents matched the selected filters.</span>
                </div>
              } @else if (importState() === 'cancelled') {
                <div class="info">
                  <mat-icon>cancel</mat-icon>
                  <span>The import was cancelled.</span>
                </div>
              } @else if (importState() === 'status-unavailable') {
                <div class="info">
                  <mat-icon>cloud_off</mat-icon>
                  <span>The import was queued, but live status is unavailable.</span>
                </div>
              } @else if (importState() === 'running') {
                <div class="info">
                  <mat-icon class="success">task_alt</mat-icon>
                  <span>The import is queued and will continue in the background.</span>
                </div>
              } @else {
                <div class="info">
                  <mat-icon class="success">done_all</mat-icon>
                  <span>Ready to import.</span>
                </div>
              }
            </div>
          </section>

          <div class="step-actions">
            <button
              mat-stroked-button
              color="primary"
              type="button"
              matStepperPrevious
              [disabled]="importBusy()"
            >
              Back
            </button>

            @if (importState() === 'running' || importState() === 'stopping') {
              <button
                mat-stroked-button
                color="warn"
                type="button"
                [disabled]="importState() === 'stopping'"
                (click)="stopImport()"
              >
                {{ importState() === 'stopping' ? 'Stopping…' : 'Stop import' }}
              </button>
            }

            @if (!hasSubmittedImport()) {
              <button
                mat-raised-button
                color="accent"
                type="button"
                [disabled]="!canProceedImport() || importBusy()"
                (click)="onSubmit()"
              >
                Import
              </button>
            } @else {
              <button mat-raised-button color="accent" type="button" (click)="onClose()">
                Close window
              </button>
            }
          </div>
        </div>
      </mat-step>
    </mat-stepper>
  </div>
</div>
